import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Typography, Link } from "@mui/material";

import { CommentBodyProps } from "../../../../types/components/props.type";
import { getProfileLink } from "../../../../../../utils/getProfileLink";
import { User } from "../../../../../../types/types";

const hasUserPattern = (text: string) => {
  const userPattern = /{{User:\d+}}/;
  return userPattern.test(text);
};

interface UserMentionProps {
  userFullName: string;
  navigateToUserProfile: () => void;
}

const UserMention = ({ userFullName, navigateToUserProfile }: UserMentionProps) => {
  return (
    <Link
      onClick={navigateToUserProfile}
      sx={{
        color: "var(--Green---100, #008B07)",
        fontFamily: "Montserrat",
        fontSize: "16px",
        fontStyle: "normal",
        fontWeight: 600,
        lineHeight: "24px",
        textDecoration: "unset",
        cursor: "pointer",
      }}
    >
      {userFullName}
    </Link>
  );
};

const extractUserId = (text: string): string | null => {
  const match = text.match(/{{User:(\d+)}}/);
  return match ? match[1] : null;
};

const CommentWithTags = ({ text, mentionedUsers }: { text: string; mentionedUsers: Partial<User>[] }) => {
  const navigate = useNavigate();
  const parts = text.split(/({{User:\d+}})/);

  const userDataMap = useMemo(() => {
    const map: Record<number, { fullName: string; profileLink: string }> = {};

    mentionedUsers.forEach((user) => {
      if (user?.id) {
        map[user.id] = {
          fullName: `${user.firstName || ""} ${user.lastName || ""}`.trim(),
          profileLink: getProfileLink(user),
        };
      }
    });

    return map;
  }, [mentionedUsers]);

  return (
    <>
      {parts.map((part, index) => {
        const userId = extractUserId(part);
        if (userId) {
          const userData = userDataMap[Number(userId)];
          if (userData) {
            return (
              <UserMention
                key={index}
                userFullName={userData.fullName}
                navigateToUserProfile={() => navigate(userData.profileLink)}
              />
            );
          }
        }
        return part;
      })}
    </>
  );
};

const CommentBody = ({ commentText, mentionedUsers }: CommentBodyProps) => {
  if (!hasUserPattern(commentText)) {
    return (
      <Typography
        sx={{
          color: "var(--Dark---1000, #111)",
          fontFamily: "Montserrat",
          fontSize: "16px",
          fontWeight: 400,
          lineHeight: "24px",
        }}
      >
        {commentText}
      </Typography>
    );
  }

  return (
    <Typography
      sx={{
        color: "var(--Dark---1000, #111)",
        fontFamily: "Montserrat",
        fontSize: "16px",
        fontWeight: 400,
        lineHeight: "24px",
      }}
    >
      <CommentWithTags text={commentText} mentionedUsers={mentionedUsers} />
    </Typography>
  );
};

export default CommentBody;
